import { useRef } from "react";
import { Download, FileText, Printer } from "lucide-react";
import { toast } from "sonner";
import {
  downloadSampleScorecardPDF,
  SAMPLE_ROWS as ROWS,
  SAMPLE_TOP3,
} from "@/lib/scorecardPdf";

const TOP3 = SAMPLE_TOP3.map((t) => `${t.title} — ${t.impact} (Effort: ${t.effort})`);

export default function SampleScorecard() {
  const ref = useRef(null);

  const handleDownload = () => {
    try {
      downloadSampleScorecardPDF();
      toast.success("Sample scorecard PDF downloaded", {
        description: "This is an example — your assessment will be tailored to your business.",
      });
    } catch (e) {
      toast.error("Could not generate PDF", { description: String(e?.message || e) });
    }
  };

  const handlePrint = () => {
    if (!ref.current) return;
    window.print();
  };

  return (
    <section
      id="scorecard"
      className="bg-white border-b border-zinc-200"
      data-testid="scorecard-section"
    >
      <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4 reveal lg:sticky lg:top-24">
            <div className="flex items-center gap-3">
              <span className="inline-block h-px w-10 bg-orange-600" />
              <span className="font-mono text-xs uppercase tracking-[0.22em] text-orange-600 font-semibold">
                Lead Magnet · Preview
              </span>
            </div>
            <h2 className="mt-6 font-display font-bold text-4xl sm:text-5xl leading-[1.02] tracking-tight text-zinc-950">
              Sample Bottleneck Scorecard.
            </h2>
            <p className="mt-6 text-zinc-600 leading-relaxed">
              This is an anonymised sample of what every Fynix assessment delivers — a one-page
              scorecard with scores, top 3 priorities and a practical roadmap. Your real
              scorecard is built from your actual workflow.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={handleDownload}
                className="inline-flex items-center justify-center gap-2 bg-zinc-950 text-white px-5 py-3 text-sm font-medium border border-zinc-950 hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(234,88,12,1)] transition-all duration-200"
                data-testid="scorecard-download-btn"
              >
                <Download size={16} /> Download PDF
              </button>
              <button
                type="button"
                onClick={handlePrint}
                className="inline-flex items-center justify-center gap-2 bg-white text-zinc-950 px-5 py-3 text-sm font-medium border border-zinc-300 hover:border-zinc-950 transition-all duration-200"
                data-testid="scorecard-print-btn"
              >
                <Printer size={16} /> Print / Save as PDF
              </button>
            </div>
          </div>

          <div className="lg:col-span-8">
            <div
              ref={ref}
              className="relative border border-zinc-950 bg-white p-6 sm:p-10 shadow-[10px_10px_0px_0px_rgba(10,10,10,1)] reveal"
              data-testid="scorecard-preview"
            >
              {/* Document header */}
              <div className="flex items-start justify-between border-b border-zinc-950 pb-5">
                <div>
                  <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500">
                    <FileText size={14} /> Document · BTK-SCORE · v1.0
                  </div>
                  <h3 className="mt-3 font-display font-bold text-2xl sm:text-3xl tracking-tight text-zinc-950">
                    Bottleneck Scorecard — Sample
                  </h3>
                </div>
                <div className="text-right">
                  <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500">
                    Overall Score
                  </div>
                  <div className="font-display font-bold text-4xl sm:text-5xl text-orange-600 leading-none mt-1">
                    51<span className="text-zinc-400 text-2xl">/100</span>
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mt-1">
                    Grade · D
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="mt-6">
                <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-2">
                  Area Breakdown
                </div>
                <ul className="divide-y divide-zinc-200 border-y border-zinc-200">
                  {ROWS.map((r) => (
                    <li key={r.area} className="grid grid-cols-[1fr_auto] gap-4 py-4">
                      <div>
                        <div className="font-display font-semibold text-zinc-950">{r.area}</div>
                        <div className="mt-1 text-sm text-zinc-600 leading-relaxed max-w-2xl">{r.note}</div>
                        <div className="mt-3 h-1.5 w-full bg-zinc-100">
                          <div
                            className="h-full bg-orange-600"
                            style={{ width: `${r.score}%` }}
                          />
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                          Score
                        </div>
                        <div className="font-display font-bold text-2xl text-zinc-950 leading-none mt-1 tabular-nums">
                          {r.score}
                        </div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-orange-600 mt-1">
                          {r.grade}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Top 3 */}
              <div className="mt-8 border border-zinc-200 p-5 bg-[#FAFAFA]">
                <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-orange-600">
                  Top 3 Priorities
                </div>
                <ol className="mt-3 space-y-2.5">
                  {TOP3.map((t, i) => (
                    <li key={i} className="grid grid-cols-[auto_1fr] gap-3">
                      <span className="font-mono text-sm text-zinc-950 tabular-nums">
                        0{i + 1}
                      </span>
                      <span className="text-sm text-zinc-800 leading-relaxed">{t}</span>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="mt-6 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">
                <span>Fynix Systems · Bottleneck · Fixed</span>
                <span>Sample · Not a client report</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
